import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { updateUser } from '../../store/slices/authSlice';
import { useEffect } from 'react';
import { updateProfile, sendVerificationCode, verifyCode } from '../../services/auth';
import { fetchAddresses, createAddress, updateAddress } from '../../store/slices/userSlice';
import { getCountries, getStates, getCities } from '../../services/address';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Avatar,
  Grid,
  Divider,
  IconButton,
  Chip,
  Snackbar,
  Alert,
  Paper,
  Switch
} from '@mui/material';
import {
  Edit,
  Save,
  Cancel,
  PhotoCamera,
  Person,
  Email,
  Phone,
  LocationOn,
  Business,
  Language,
  LinkedIn,
  Twitter,
  Add
} from '@mui/icons-material';
import Select from 'react-select';
import PageMeta from '../../components/PageMeta';

function formatDate(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date)) return dateString;
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function formatLastLogin(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date)) return dateString;
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  }).replace(',', ' :');
}

// Helper for country flag emoji
function getFlagEmoji(countryCode) {
  if (!countryCode) return '';
  return countryCode
    .toUpperCase()
    .replace(/./g, char =>
      String.fromCodePoint(127397 + char.charCodeAt())
    );
}

const selectStyles = {
  menuPortal: base => ({ ...base, zIndex: 9999 }),
  container: base => ({ ...base, width: '100%' }),
  control: base => ({ ...base, minWidth: 200, width: '100%' }),
};

const Profile = () => {
  const dispatch = useDispatch();
  const user = useSelector(state => state.auth.user);
  const rawAddresses = useSelector(state => state.user.addresses);
  const addresses = Array.isArray(rawAddresses) ? rawAddresses : [];
  const [isEditing, setIsEditing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Check if user has existing email/phone
  const hasExistingEmail = user?.email && user.email.trim() !== '';
  const hasExistingPhone = user?.phone && user.phone.trim() !== '';
  
  // Verification states
  const [emailVerification, setEmailVerification] = useState({
    codeSent: false,
    codeVerified: false,
    code: '',
    loading: false
  });
  
  const [phoneVerification, setPhoneVerification] = useState({
    codeSent: false,
    codeVerified: false,
    code: '',
    loading: false
  });
  
  // Initialize profile state from Redux user
  const [profile, setProfile] = useState({
    fullname: user?.fullname || '',
    email: user?.email || '',
    phone: user?.phone || '',
    position: user?.position || '',
    created_at: user?.created_at || '',
    lastLogin: user?.last_login || '',
    status: user?.is_active ? 'Active' : 'Inactive',
    address: {
      street: user?.address?.street || '',
      city: user?.address?.city || '',
      state: user?.address?.state || '',
      zipCode: user?.address?.zipCode || '',
      country: user?.address?.country || '',
    },
  });
  const [formData, setFormData] = useState(profile);

  // Address state
  const [showNewAddressForm, setShowNewAddressForm] = useState(false);
  const [newAddress, setNewAddress] = useState({
    address_line1: '', city_id: '', state_id: '', country_id: '', zipCode: '', is_default: false, receiver_phone: '', receiver_name: ''
  });
  const [addressForms, setAddressForms] = useState([]);

  // Add validation helper
  const validateProfile = (data) => {
    const errors = {};
    if (!data.fullname || data.fullname.trim() === '') errors.fullname = 'Full name is required';
    if (!data.email || data.email.trim() === '') errors.email = 'Email is required';
    else if (!/^\S+@\S+\.\S+$/.test(data.email)) errors.email = 'Invalid email address';
    if (!data.phone || data.phone.trim() === '' || data.phone === '+234') errors.phone = 'Phone is required';
    if (!data.position || data.position.trim() === '') errors.position = 'Position is required';
    // Address fields are now optional
    return errors;
  };

  // Remove previous formErrors state and validation logic
  // Add validation effect logic for border color
  // Remove getBorderColor and any border color logic
  // Ensure the following logic is present:
  // - handleSave: shows specific error fields in snackbar
  // - phone/email fields: locked if not empty and not verified, editable if empty or verified
  // - verification code logic for both fields

  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  useEffect(() => {
    async function fetchCountries() {
      const countryData = await getCountries();
      setCountries(countryData.results || countryData);
    }
    fetchCountries();
  }, []);

  // Update fetchStatesForCountry and fetchCitiesForState to use query params
  const fetchStatesForCountry = async (countryId) => {
    if (!countryId) {
      setStates([]);
      return;
    }
    const stateData = await getStates({ country_id: countryId });
    setStates(stateData.results || stateData);
  };
  const fetchCitiesForState = async (stateId) => {
    if (!stateId) {
      setCities([]);
      return;
    }
    const cityData = await getCities({ state_id: stateId });
    setCities(cityData.results || cityData);
  };

  useEffect(() => {
    dispatch(fetchAddresses());
    // eslint-disable-next-line
  }, [user, dispatch]);

  useEffect(() => {
    setProfile({
      fullname: user?.fullname || '',
      email: user?.email || '',
      phone: user?.phone || '',
      position: user?.position || '',
      created_at: user?.created_at || '',
      lastLogin: user?.last_login || '',
      status: user?.is_active ? 'Active' : 'Inactive',
      address: {
        street: user?.address?.street || '',
        city: user?.address?.city || '',
        state: user?.address?.state || '',
        zipCode: user?.address?.zipCode || '',
        country: user?.address?.country || '',
      },
    });
    setFormData({
      fullname: user?.fullname || '',
      email: user?.email || '',
      phone: user?.phone || '',
      position: user?.position || '',
      created_at: user?.created_at || '',
      lastLogin: user?.last_login || '',
      status: user?.is_active ? 'Active' : 'Inactive',
      address: {
        street: user?.address?.street || '',
        city: user?.address?.city || '',
        state: user?.address?.state || '',
        zipCode: user?.address?.zipCode || '',
        country: user?.address?.country || '',
      },
    });
  }, [user, dispatch]);

  useEffect(() => {
    setAddressForms(addresses.map(addr => ({
      ...addr,
      zipCode: addr.zipCode || addr.postal_code || '', // Map postal_code to zipCode for form
      receiver_phone: addr.receiver_phone || '',
      receiver_name: addr.receiver_name || ''
    })));
  }, [addresses]);

  // Helper to update all other addresses to is_default=false
  const unsetOtherDefaults = async (currentId) => {
    for (const addr of addresses) {
      if (addr.id !== currentId && addr.is_default) {
        await dispatch(updateAddress({ id: addr.id, addressData: { ...addr, is_default: false } }));
      }
    }
  };

  // Update handleNewAddressSubmit
  const handleNewAddressSubmit = async () => {
    if (!newAddress.receiver_phone || newAddress.receiver_phone.trim() === '') {
      setSnackbar({ open: true, message: 'Receiver phone number is required', severity: 'error' });
      return;
    }
    let payload = {
      address_line1: newAddress.address_line1,
      city_id: newAddress.city_id,
      postal_code: newAddress.zipCode,
      receiver_phone: newAddress.receiver_phone,
      receiver_name: newAddress.receiver_name,
      is_default: newAddress.is_default,
    };
    // If this is the first address, always set is_default true
    if (addresses.length === 0) {
      payload.is_default = true;
    }
    const result = await dispatch(createAddress(payload));
    dispatch(fetchAddresses());
    setShowNewAddressForm(false);
    setNewAddress({ address_line1: '', city_id: '', state_id: '', country_id: '', zipCode: '', is_default: false, receiver_phone: '', receiver_name: '' });
    setSnackbar({ open: true, message: 'Address added successfully!', severity: 'success' });
    // If user selected set as default, unset other defaults
    if (addresses.length > 0 && payload.is_default && result.payload && result.payload.id) {
      await unsetOtherDefaults(result.payload.id);
      dispatch(fetchAddresses());
    }
  };

  // Update handleAddressChange for is_default toggle
  const handleAddressChange = async (idx, field, value) => {
    if (field === 'is_default') {
      // If only one address, do not allow toggling off
      if (addresses.length === 1) {
        // Always keep is_default true for the only address
        setAddressForms(forms => forms.map((f, i) => i === idx ? { ...f, is_default: true } : f));
        setSnackbar({ open: true, message: 'You must have at least one default address.', severity: 'info' });
        return;
      }
      // If toggling on, update backend and unset others
      if (value === true) {
        const addr = addressForms[idx];
        const payload = {
          address_line1: addr.address_line1,
          city_id: addr.city_id,
          postal_code: addr.zipCode,
          receiver_phone: addr.receiver_phone || '',
          receiver_name: addr.receiver_name || '',
          is_default: true,
        };
        await dispatch(updateAddress({ id: addr.id, addressData: payload }));
        await unsetOtherDefaults(addr.id);
        dispatch(fetchAddresses());
        setSnackbar({ open: true, message: 'Default address updated!', severity: 'success' });
      } else {
        // If toggling off and more than one address, allow
        const addr = addressForms[idx];
        const payload = {
          address_line1: addr.address_line1,
          city_id: addr.city_id,
          postal_code: addr.zipCode,
          receiver_phone: addr.receiver_phone || '',
          receiver_name: addr.receiver_name || '',
          is_default: false,
        };
        await dispatch(updateAddress({ id: addr.id, addressData: payload }));
        dispatch(fetchAddresses());
        setSnackbar({ open: true, message: 'Address is no longer default.', severity: 'info' });
      }
    }
    setAddressForms(forms => forms.map((f, i) => i === idx ? { ...f, [field]: value } : f));
  };

  // Restore handleNewAddressChange as a standalone function
  const handleNewAddressChange = (field, value) => {
    setNewAddress(addr => {
      if (field === 'country_id') {
        fetchStatesForCountry(value);
        setCities([]);
        return { ...addr, country_id: value, state_id: '', city_id: '' };
      }
      if (field === 'state_id') {
        // Always fetch cities for the selected state
        fetchCitiesForState(value);
        return { ...addr, state_id: value, city_id: '' };
      }
      return { ...addr, [field]: value };
    });
  };

  // Submit handlers
  const handleAddressSubmit = async (idx) => {
    const addr = addressForms[idx];
    const payload = {
      address_line1: addr.address_line1,
      city_id: addr.city_id,
      postal_code: addr.zipCode,
      receiver_phone: addr.receiver_phone || '',
      receiver_name: addr.receiver_name || '',
      is_default: addr.is_default,
    };
    await dispatch(updateAddress({ id: addr.id, addressData: payload }));
    dispatch(fetchAddresses());
    setSnackbar({ open: true, message: 'Address updated successfully!', severity: 'success' });
  };
  // Update handleNewAddressSubmit to send IDs
  // const handleNewAddressSubmit = async () => {
  //   if (!newAddress.receiver_phone || newAddress.receiver_phone.trim() === '') {
  //     setSnackbar({ open: true, message: 'Receiver phone number is required', severity: 'error' });
  //     return;
  //   }
  //   const payload = {
  //     address_line1: newAddress.address_line1,
  //     city_id: newAddress.city_id,
  //     postal_code: newAddress.zipCode,
  //     receiver_phone: newAddress.receiver_phone,
  //     receiver_name: newAddress.receiver_name,
  //     is_default: newAddress.is_default,
  //   };
  //   await dispatch(createAddress(payload));
  //   dispatch(fetchAddresses());
  //   setShowNewAddressForm(false);
  //   setNewAddress({ address_line1: '', city_id: '', state_id: '', country_id: '', zipCode: '', is_default: false, receiver_phone: '', receiver_name: '' });
  //   setSnackbar({ open: true, message: 'Address added successfully!', severity: 'success' });
  // };

  // Render address section
  const renderAddressSection = () => {
    if (!addresses || addresses.length === 0) {
      // No address: show one address info box with selects
      return (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6">Address Information</Typography>
            <Grid container spacing={2}>
              {['address_line1', 'country_id', 'state_id', 'city_id', 'zipCode'].map(field => (
                <Grid size={{ xs: 12, md: 6 }} key={field}>
                  {field === 'country_id' ? (
                    <Select
                      options={countries.map(c => ({ value: c.id, label: c.name, code: c.code }))}
                      value={countries.find(c => c.id === newAddress.country_id) && { value: newAddress.country_id, label: countries.find(c => c.id === newAddress.country_id)?.name, code: countries.find(c => c.id === newAddress.country_id)?.code }}
                      onChange={option => handleNewAddressChange('country_id', option.value)}
                      isSearchable
                      placeholder="Select Country"
                      formatOptionLabel={option => (
                        <span><span style={{ fontSize: 18, marginRight: 8 }}>{getFlagEmoji(option.code)}</span>{option.label}</span>
                      )}
                      menuPortalTarget={document.body}
                      styles={selectStyles}
                    />
                  ) : field === 'state_id' ? (
                    <Select
                      options={states.map(s => ({ value: s.id, label: s.name, country: s.country }))}
                      value={states.find(s => s.id === newAddress.state_id) && { value: newAddress.state_id, label: states.find(s => s.id === newAddress.state_id)?.name, country: states.find(s => s.id === newAddress.state_id)?.country }}
                      onChange={option => handleNewAddressChange('state_id', option.value)}
                      isSearchable
                      placeholder="Select State"
                      menuPortalTarget={document.body}
                      styles={selectStyles}
                    />
                  ) : field === 'city_id' ? (
                    <Select
                      options={cities.map(ci => ({ value: ci.id, label: ci.name, state: ci.state }))}
                      value={cities.find(ci => ci.id === newAddress.city_id) && { value: newAddress.city_id, label: cities.find(ci => ci.id === newAddress.city_id)?.name, state: cities.find(ci => ci.id === newAddress.city_id)?.state }}
                      onChange={option => handleNewAddressChange('city_id', option.value)}
                      isSearchable
                      placeholder="Select City"
                      menuPortalTarget={document.body}
                      styles={selectStyles}
                    />
                  ) : (
                    <TextField
                      label={field === 'zipCode' ? 'ZIP Code' : 'Street'}
                      value={newAddress[field] || ''}
                      onChange={e => handleNewAddressChange(field, e.target.value)}
                      fullWidth
                    />
                  )}
                </Grid>
              ))}
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Receiver Name"
                  value={newAddress.receiver_name || ''}
                  onChange={e => handleNewAddressChange('receiver_name', e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Receiver Phone"
                  value={newAddress.receiver_phone || ''}
                  onChange={e => handleNewAddressChange('receiver_phone', e.target.value)}
                  fullWidth
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Box display="flex" alignItems="center">
                  <Switch
                    checked={!!newAddress.is_default}
                    onChange={e => handleNewAddressChange('is_default', e.target.checked)}
                    color="primary"
                  />
                  <Typography>Set as default</Typography>
                </Box>
              </Grid>
            </Grid>
            <Button
              variant="contained"
              color="primary"
              sx={{ mt: 2 }}
              onClick={handleNewAddressSubmit}
            >
              Save Address
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              sx={{ mt: 2, ml: 2 }}
              onClick={() => setShowNewAddressForm(false)}
            >
              Cancel
            </Button>
          </CardContent>
        </Card>
      );
    }
    // User has addresses: show all, and add button to add more
    return (
      <Box>
        {addressForms.map((addr, idx) => (
          <Card sx={{ mb: 2 }} key={addr.id || idx}>
            <CardContent>
              <Typography variant="h6">Address {idx + 1}</Typography>
              <Grid container spacing={2}>
                {['address_line1', 'country_id', 'state_id', 'city_id', 'zipCode'].map(field => (
                  <Grid size={{ xs: 12, sm: 6 }} key={field}>
                    {field === 'country_id' ? (
                      <Select
                        options={countries.map(c => ({ value: c.id, label: c.name, code: c.code }))}
                        value={countries.find(c => c.id === addr.country_id) && { value: addr.country_id, label: countries.find(c => c.id === addr.country_id)?.name, code: countries.find(c => c.id === addr.country_id)?.code }}
                        onChange={option => handleAddressChange(idx, 'country_id', option.value)}
                        isSearchable
                        placeholder="Select Country"
                        formatOptionLabel={option => (
                          <span><span style={{ fontSize: 18, marginRight: 8 }}>{getFlagEmoji(option.code)}</span>{option.label}</span>
                        )}
                        menuPortalTarget={document.body}
                        styles={selectStyles}
                      />
                    ) : field === 'state_id' ? (
                      <Select
                        options={states.filter(s => !addr.country_id || s.country === addr.country_id).map(s => ({ value: s.id, label: s.name, country: s.country }))}
                        value={states.find(s => s.id === addr.state_id) && { value: addr.state_id, label: states.find(s => s.id === addr.state_id)?.name, country: states.find(s => s.id === addr.state_id)?.country }}
                        onChange={option => handleAddressChange(idx, 'state_id', option.value)}
                        isSearchable
                        placeholder="Select State"
                        menuPortalTarget={document.body}
                        styles={selectStyles}
                      />
                    ) : field === 'city_id' ? (
                      <Select
                        options={cities.filter(ci => !addr.state_id || ci.state === addr.state_id).map(ci => ({ value: ci.id, label: ci.name, state: ci.state }))}
                        value={cities.find(ci => ci.id === addr.city_id) && { value: addr.city_id, label: cities.find(ci => ci.id === addr.city_id)?.name, state: cities.find(ci => ci.id === addr.city_id)?.state }}
                        onChange={option => handleAddressChange(idx, 'city_id', option.value)}
                        isSearchable
                        placeholder="Select City"
                        menuPortalTarget={document.body}
                        styles={selectStyles}
                      />
                    ) : (
                      <TextField
                        label={field === 'zipCode' ? 'ZIP Code' : 'Street'}
                        value={addr[field] || ''}
                        onChange={e => handleAddressChange(idx, field, e.target.value)}
                        fullWidth
                      />
                    )}
                  </Grid>
                ))}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Receiver Name"
                    value={addr.receiver_name || ''}
                    onChange={e => handleAddressChange(idx, 'receiver_name', e.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Receiver Phone"
                    value={addr.receiver_phone || ''}
                    onChange={e => handleAddressChange(idx, 'receiver_phone', e.target.value)}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box display="flex" alignItems="center">
                    <Switch
                      checked={!!addr.is_default}
                      onChange={e => handleAddressChange(idx, 'is_default', e.target.checked)}
                      color="primary"
                      disabled={addresses.length === 1}
                    />
                    <Typography>Set as default</Typography>
                  </Box>
                </Grid>
              </Grid>
              <Button
                variant="contained"
                color="primary"
                sx={{ mt: 2 }}
                onClick={() => handleAddressSubmit(idx)}
              >
                Update Address
              </Button>
            </CardContent>
          </Card>
        ))}
        {canAddAddress && !showNewAddressForm && (
          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={() => setShowNewAddressForm(true)}
            sx={{
              mb: 2,
              border: '1px solid #DC143C',
              color: '#DC143C',
              background: '#fff',
              fontWeight: 'bold',
              '&:hover': { background: '#f5f5f5', borderColor: '#DC143C', color: '#DC143C' }
            }}
          >
            Add Address
          </Button>
        )}
        {showNewAddressForm && canAddAddress && (
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6">Add New Address</Typography>
              <Grid container spacing={2}>
                {['address_line1', 'country_id', 'state_id', 'city_id', 'zipCode'].map(field => (
                  <Grid size={{ xs: 12, sm: 6 }} key={field}>
                    {field === 'country_id' ? (
                      <Select
                        options={countries.map(c => ({ value: c.id, label: c.name, code: c.code }))}
                        value={countries.find(c => c.id === newAddress.country_id) && { value: newAddress.country_id, label: countries.find(c => c.id === newAddress.country_id)?.name, code: countries.find(c => c.id === newAddress.country_id)?.code }}
                        onChange={option => handleNewAddressChange('country_id', option.value)}
                        isSearchable
                        placeholder="Select Country"
                        formatOptionLabel={option => (
                          <span><span style={{ fontSize: 18, marginRight: 8 }}>{getFlagEmoji(option.code)}</span>{option.label}</span>
                        )}
                        menuPortalTarget={document.body}
                        styles={selectStyles}
                      />
                    ) : field === 'state_id' ? (
                      <Select
                        options={states.filter(s => !newAddress.country_id || s.country === newAddress.country_id).map(s => ({ value: s.id, label: s.name, country: s.country }))}
                        value={states.find(s => s.id === newAddress.state_id) && { value: newAddress.state_id, label: states.find(s => s.id === newAddress.state_id)?.name, country: states.find(s => s.id === newAddress.state_id)?.country }}
                        onChange={option => handleNewAddressChange('state_id', option.value)}
                        isSearchable
                        placeholder="Select State"
                        menuPortalTarget={document.body}
                        styles={selectStyles}
                      />
                    ) : field === 'city_id' ? (
                      <Select
                        options={cities.filter(ci => !newAddress.state_id || ci.state === newAddress.state_id).map(ci => ({ value: ci.id, label: ci.name, state: ci.state }))}
                        value={cities.find(ci => ci.id === newAddress.city_id) && { value: newAddress.city_id, label: cities.find(ci => ci.id === newAddress.city_id)?.name, state: cities.find(ci => ci.id === newAddress.city_id)?.state }}
                        onChange={option => handleNewAddressChange('city_id', option.value)}
                        isSearchable
                        placeholder="Select City"
                        menuPortalTarget={document.body}
                        styles={selectStyles}
                      />
                    ) : (
                      <TextField
                        label={field === 'zipCode' ? 'ZIP Code' : 'Street'}
                        value={newAddress[field] || ''}
                        onChange={e => handleNewAddressChange(field, e.target.value)}
                        fullWidth
                      />
                    )}
                  </Grid>
                ))}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Receiver Name"
                    value={newAddress.receiver_name || ''}
                    onChange={e => handleNewAddressChange('receiver_name', e.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Receiver Phone"
                    value={newAddress.receiver_phone || ''}
                    onChange={e => handleNewAddressChange('receiver_phone', e.target.value)}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box display="flex" alignItems="center">
                    <Switch
                      checked={!!newAddress.is_default}
                      onChange={e => handleNewAddressChange('is_default', e.target.checked)}
                      color="primary"
                    />
                    <Typography>Set as default</Typography>
                  </Box>
                </Grid>
              </Grid>
              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleNewAddressSubmit}
                  sx={{
                    border: '1px solid #DC143C',
                    color: '#DC143C',
                    background: '#fff',
                    fontWeight: 'bold',
                    '&:hover': { background: '#f5f5f5', borderColor: '#DC143C', color: '#DC143C' }
                  }}
                >
                  Save Address
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setShowNewAddressForm(false)}
                  sx={{
                    border: '1px solid #DC143C',
                    color: '#DC143C',
                    background: '#fff',
                    fontWeight: 'bold',
                    '&:hover': { background: '#f5f5f5', borderColor: '#DC143C', color: '#DC143C' }
                  }}
                >
                  Remove
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}
        {!canAddAddress && (
          <Alert severity="info" sx={{ mb: 2 }}>
            You have reached the maximum of 5 addresses.
          </Alert>
        )}
      </Box>
    );
  };

  const handleEdit = () => {
    setFormData(profile);
    setIsEditing(true);
    // Reset verification states when starting to edit
    setEmailVerification({ codeSent: false, codeVerified: false, code: '', loading: false });
    setPhoneVerification({ codeSent: false, codeVerified: false, code: '', loading: false });
  };

  const handleCancel = () => {
    setFormData(profile);
    setIsEditing(false);
    // Reset verification states
    setEmailVerification({ codeSent: false, codeVerified: false, code: '', loading: false });
    setPhoneVerification({ codeSent: false, codeVerified: false, code: '', loading: false });
  };

  const handleSave = async () => {
    const errors = validateProfile(formData);
    if (Object.keys(errors).length > 0) {
      // Format error message: 'Street is required - City is required'
      const errorMessage = Object.values(errors).join(' - ');
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
      return;
    }
    try {
      // Prepare data for server update
      const updateData = {
        fullname: formData.fullname,
        position: formData.position
      };
      
      // Include email if it was verified or if user doesn't have existing email
      if (emailVerification.codeVerified || !hasExistingEmail) {
        updateData.email = formData.email;
      }
      
      // Include phone if it was verified or if user doesn't have existing phone
      if (phoneVerification.codeVerified || !hasExistingPhone) {
        updateData.phone = formData.phone;
      }
      
      // Call the API to update profile
      const response = await updateProfile(updateData);
      
      // Update Redux state with new user data
      if (response.user) {
        dispatch(updateUser(response.user));
      }
      
      setProfile(formData);
      setIsEditing(false);
      setSnackbar({ open: true, message: 'Profile updated successfully!', severity: 'success' });
      
      // Reset verification states
      setEmailVerification({ codeSent: false, codeVerified: false, code: '', loading: false });
      setPhoneVerification({ codeSent: false, codeVerified: false, code: '', loading: false });
      
    } catch (error) {
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    }
  };

  const handleSendEmailCode = async () => {
    try {
      setEmailVerification(prev => ({ ...prev, loading: true }));
      // Use the email from formData (could be existing or new)
      await sendVerificationCode({ type: 'email', email: formData.email });
      setEmailVerification(prev => ({ ...prev, codeSent: true, loading: false }));
      setSnackbar({ open: true, message: 'Verification code sent to your email!', severity: 'success' });
    } catch (error) {
      setEmailVerification(prev => ({ ...prev, loading: false }));
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    }
  };

  const handleSendPhoneCode = async () => {
    try {
      setPhoneVerification(prev => ({ ...prev, loading: true }));
      const phone = formData.phone.replace('+', ''); // Remove + for API
      await sendVerificationCode({ type: 'sms', phone });
      setPhoneVerification(prev => ({ ...prev, codeSent: true, loading: false }));
      setSnackbar({ open: true, message: 'Verification code sent to your phone!', severity: 'success' });
    } catch (error) {
      setPhoneVerification(prev => ({ ...prev, loading: false }));
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    }
  };

  const handleVerifyEmailCode = async () => {
    try {
      setEmailVerification(prev => ({ ...prev, loading: true }));
      await verifyCode({ type: 'email', email: formData.email, code: emailVerification.code });
      setEmailVerification(prev => ({ ...prev, codeVerified: true, loading: false }));
      setSnackbar({ open: true, message: 'Email verified successfully!', severity: 'success' });
    } catch (error) {
      setEmailVerification(prev => ({ ...prev, loading: false }));
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    }
  };

  const handleVerifyPhoneCode = async () => {
    try {
      setPhoneVerification(prev => ({ ...prev, loading: true }));
      const phone = formData.phone.replace('+', ''); // Remove + for API
      await verifyCode({ type: 'sms', phone, code: phoneVerification.code });
      setPhoneVerification(prev => ({ ...prev, codeVerified: true, loading: false }));
      setSnackbar({ open: true, message: 'Phone verified successfully!', severity: 'success' });
    } catch (error) {
      setPhoneVerification(prev => ({ ...prev, loading: false }));
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    }
  };

  const handleChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleAvatarChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setFormData(prev => ({
          ...prev,
          avatar: e.target.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  // Smart field locking logic
  const isEmailLocked = isEditing && hasExistingEmail && !emailVerification.codeVerified;
  const isEmailEditable = isEditing && (!hasExistingEmail || emailVerification.codeVerified);
  const showEmailVerification = isEditing && (hasExistingEmail || (formData.email && formData.email.trim() !== ''));

  const isPhoneLocked = isEditing && hasExistingPhone && !phoneVerification.codeVerified;
  const isPhoneEditable = isEditing && (!hasExistingPhone || phoneVerification.codeVerified);
  const showPhoneVerification = isEditing && (hasExistingPhone || (formData.phone && formData.phone.trim() !== '' && formData.phone !== '+234'));

  const maxAddresses = 5;
  const canAddAddress = addresses.length < maxAddresses;

  // 1. Add useEffect to fetch states/cities for edit forms
  useEffect(() => {
    // For new address form
    if (showNewAddressForm && newAddress.country_id) {
      fetchStatesForCountry(newAddress.country_id);
    }
    if (showNewAddressForm && newAddress.state_id) {
      fetchCitiesForState(newAddress.state_id);
    }
    // For edit address forms: fetch states for each country, and cities for each state (avoid duplicate fetches)
    const fetchedStates = new Set();
    const fetchedCities = new Set();
    addressForms.forEach(addr => {
      if (addr.country_id && !fetchedStates.has(addr.country_id)) {
        fetchStatesForCountry(addr.country_id);
        fetchedStates.add(addr.country_id);
      }
      if (addr.state_id && !fetchedCities.has(addr.state_id)) {
        fetchCitiesForState(addr.state_id);
        fetchedCities.add(addr.state_id);
      }
    });
    // eslint-disable-next-line
  }, [showNewAddressForm, newAddress.country_id, newAddress.state_id, addressForms]);

  return (
    <>
      <PageMeta
        title="Profile"
        description="View and update your profile information and contact details for the Checkstall Import admin account."
      />
      <Box sx={{
      minHeight: '100vh',
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold', color: '#DC143C' }}>
        Profile
      </Typography>

      <Grid container spacing={3}>
        {/* Profile Header */}
        <Grid size={{ xs: 12 }}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #DC143C 0%, #FF6B6B 100%)',
            color: 'white',
            mb: 3
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <Box sx={{ position: 'relative' }}>
                  <Avatar
                    src={formData.avatar}
                    sx={{ width: 120, height: 120, border: '4px solid white' }}
                  />
                  {isEditing && (
                    <IconButton
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        right: 0,
                        backgroundColor: 'white',
                        '&:hover': { backgroundColor: '#f5f5f5' }
                      }}
                      component="label"
                    >
                      <PhotoCamera sx={{ color: '#DC143C' }} />
                      <input
                        hidden
                        accept="image/*"
                        type="file"
                        onChange={handleAvatarChange}
                      />
                    </IconButton>
                  )}
                </Box>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {formData.fullname}
                  </Typography>
                  <Typography variant="h6" sx={{ mb: 1, opacity: 0.9 }}>
                    {formData.position}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Chip 
                      label={formData.status} 
                      size="small" 
                      sx={{ 
                        backgroundColor: '#4CAF50', 
                        color: 'white',
                        fontWeight: 'bold'
                      }} 
                    />
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      Member since {formatDate(formData.created_at)}
                    </Typography>
                  </Box>
                </Box>
                <Box>
                  {!isEditing ? (
                    <Button
                      variant="contained"
                      startIcon={<Edit />}
                      onClick={handleEdit}
                      sx={{ 
                        backgroundColor: 'white', 
                        color: '#DC143C',
                        '&:hover': { backgroundColor: '#f5f5f5' }
                      }}
                    >
                      Edit Profile
                    </Button>
                  ) : (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="contained"
                        startIcon={<Save />}
                        onClick={handleSave}
                        sx={{ 
                          backgroundColor: 'white', 
                          color: '#DC143C',
                          '&:hover': { backgroundColor: '#f5f5f5' }
                        }}
                      >
                        Save
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<Cancel />}
                        onClick={handleCancel}
                        sx={{ 
                          borderColor: 'white', 
                          color: 'white',
                          '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.1)' }
                        }}
                      >
                        Cancel
                      </Button>
                    </Box>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Personal Information */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)', border: '1px solid #e0e0e0' }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold', color: '#DC143C' }}>
                Personal Information
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Full Name"
                    value={formData.fullname}
                    onChange={(e) => handleChange('fullname', e.target.value)}
                    disabled={!isEditing}
                    InputProps={{
                      startAdornment: <Person sx={{ mr: 1, color: '#DC143C' }} />
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    disabled={!isEmailEditable}
                    InputProps={{
                      startAdornment: <Email sx={{ mr: 1, color: '#DC143C' }} />
                    }}
                  />
                  {showEmailVerification && !emailVerification.codeVerified && (
                    <Box sx={{ mt: 1 }}>
                      {!emailVerification.codeSent ? (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={handleSendEmailCode}
                          disabled={emailVerification.loading || !formData.email}
                          sx={{ color: '#DC143C', borderColor: '#DC143C' }}
                        >
                          {emailVerification.loading ? 'Sending...' : hasExistingEmail ? 'Verify Existing Email' : 'Send Code to New Email'}
                        </Button>
                      ) : (
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <TextField
                            size="small"
                            placeholder="Enter code"
                            value={emailVerification.code}
                            onChange={(e) => setEmailVerification(prev => ({ ...prev, code: e.target.value }))}
                            sx={{ width: 120 }}
                          />
                          <Button
                            size="small"
                            variant="contained"
                            onClick={handleVerifyEmailCode}
                            disabled={emailVerification.loading || !emailVerification.code}
                            sx={{ backgroundColor: '#DC143C' }}
                          >
                            {emailVerification.loading ? 'Verifying...' : 'Verify'}
                          </Button>
                        </Box>
                      )}
                    </Box>
                  )}
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Phone"
                    value={formData.phone || '+234'}
                    onChange={(e) => {
                      let value = e.target.value;
                      // If the field was empty and user starts typing, ensure it starts with +234
                      if (!formData.phone && !value.startsWith('+234')) {
                        value = '+234' + value.replace(/^\+?234?/, '');
                      }
                      setFormData(prev => ({ ...prev, phone: value }));
                    }}
                    disabled={!isPhoneEditable}
                    InputProps={{
                      startAdornment: <Phone sx={{ mr: 1, color: '#DC143C' }} />
                    }}
                  />
                  {showPhoneVerification && !phoneVerification.codeVerified && (
                    <Box sx={{ mt: 1 }}>
                      {!phoneVerification.codeSent ? (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={handleSendPhoneCode}
                          disabled={phoneVerification.loading || !formData.phone || formData.phone === '+234'}
                          sx={{ color: '#DC143C', borderColor: '#DC143C' }}
                        >
                          {phoneVerification.loading ? 'Sending...' : hasExistingPhone ? 'Verify Existing Phone' : 'Send Code to New Phone'}
                        </Button>
                      ) : (
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <TextField
                            size="small"
                            placeholder="Enter code"
                            value={phoneVerification.code}
                            onChange={(e) => setPhoneVerification(prev => ({ ...prev, code: e.target.value }))}
                            sx={{ width: 120 }}
                          />
                          <Button
                            size="small"
                            variant="contained"
                            onClick={handleVerifyPhoneCode}
                            disabled={phoneVerification.loading || !phoneVerification.code}
                            sx={{ backgroundColor: '#DC143C' }}
                          >
                            {phoneVerification.loading ? 'Verifying...' : 'Verify'}
                          </Button>
                        </Box>
                      )}
                    </Box>
                  )}
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Position"
                    value={formData.position}
                    onChange={(e) => handleChange('position', e.target.value)}
                    disabled={!isEditing}
                    InputProps={{
                      startAdornment: <Business sx={{ mr: 1, color: '#DC143C' }} />
                    }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Address Information */}
        <Grid size={{ xs: 12, md: 6 }}>
          {renderAddressSection()}
        </Grid>

        {/* Account Information */}
        <Grid size={{ xs: 12 }}>
          <Card sx={{ background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)', border: '1px solid #e0e0e0' }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold', color: '#DC143C' }}>
                Account Information
              </Typography>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#f8f9fa' }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#DC143C' }}>
                      {formatDate(formData.created_at)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Member Since
                    </Typography>
                  </Paper>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#f8f9fa' }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#DC143C' }}>
                      {formData.lastLogin ? formatLastLogin(formData.lastLogin) : 'Unknown'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Last Login
                    </Typography>
                  </Paper>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#f8f9fa' }}>
                    <Chip 
                      label={formData.status} 
                      sx={{ 
                        backgroundColor: '#4CAF50', 
                        color: 'white',
                        fontWeight: 'bold'
                      }} 
                    />
                    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                      Account Status
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={3000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
    </>
  );
};

export default Profile; 
